import React, { useState, useEffect, useCallback } from 'react';

const BOOT_LINES = [
  { t: 0,    text: 'BLADE OS // NEURAL QUANTUM KERNEL v4.2', tone: 'cyan' },
  { t: 220,  text: 'Copyright (c) 2026 CYBERNETIC ARCHITECTURE // BLADE LABS', tone: 'muted' },
  { t: 450,  text: 'CPU: QUANTUM-X9 (128 Cores @ 9.80GHz) | RAM: 512GB Q-RAM | OS: BLADE-RT 2077', tone: 'cyan' },
  { t: 700,  text: '', tone: 'muted' },
  { t: 900,  text: '[OK] Calibrating neural interface and holographic sub-matrix', tone: 'green' },
  { t: 1150, text: '[OK] Synchronizing fullstack architectures & projects vault', tone: 'green' },
  { t: 1400, text: '[OK] Mounting cyberdeck shell, crypto-term & interactive desktop', tone: 'green' },
  { t: 1650, text: '[OK] Establishing optical satellite link // 100 Gbps quantum mesh', tone: 'green' },
  { t: 1900, text: '', tone: 'muted' },
  { t: 2050, text: '>>> Neural link established. Welcome back, Blade...', tone: 'pink' },
];

const TONE = {
  cyan: 'text-[#00f0ff]',
  pink: 'text-[#ff2a6d]',
  green: 'text-[#39ff14]',
  violet: 'text-[#a855f7]',
  muted: 'text-[#7d8b9e]',
};

export default function BootScreen({ onComplete }) {
  const [progress, setProgress] = useState(0);
  const [visibleLines, setVisibleLines] = useState([]);
  const [done, setDone] = useState(false);
  const [exiting, setExiting] = useState(false);
  const onCompleteRef = React.useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const handleFinish = useCallback(() => {
    setDone(true);
    setExiting(true);
    setTimeout(() => {
      onCompleteRef.current?.();
    }, 300);
  }, []);

  useEffect(() => {
    let mounted = true;
    let finished = false;
    const start = Date.now();
    const DURATION = 2400;
    const timeouts = [];

    const iv = setInterval(() => {
      if (!mounted) return;
      const elapsed = Date.now() - start;
      const p = Math.min(100, (elapsed / DURATION) * 100);
      setProgress(p);
    }, 25);

    BOOT_LINES.forEach((l) => {
      timeouts.push(setTimeout(() => {
        if (mounted) setVisibleLines((p) => [...p, l]);
      }, l.t));
    });

    const triggerFinish = () => {
      if (!mounted || finished) return;
      finished = true;
      clearInterval(iv);
      timeouts.forEach(clearTimeout);
      handleFinish();
    };

    timeouts.push(setTimeout(triggerFinish, DURATION));

    const onKeyDown = (e) => {
      if (e.key === ' ' || e.key === 'Enter' || e.key === 'Escape') {
        triggerFinish();
      }
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      mounted = false;
      clearInterval(iv);
      timeouts.forEach(clearTimeout);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [handleFinish]);

  if (done) {
    return (
      <div
        className="absolute inset-0 z-[800] bg-[#020008] pointer-events-none transition-opacity duration-300"
        style={{ opacity: exiting ? 0 : 1 }}
      />
    );
  }

  return (
    <div
      onClick={handleFinish}
      className="absolute inset-0 z-[800] bg-[#020008]/95 backdrop-blur-md font-mono text-xs md:text-sm flex flex-col p-5 sm:p-7 md:p-8 overflow-hidden select-none cursor-pointer"
      title="Clique ou pressione ESPAÇO para pular"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[repeating-linear-gradient(0deg,transparent,transparent_2px,rgba(0,240,255,0.03)_2px,rgba(0,240,255,0.03)_4px)]" />
      </div>

      {/* HEADER BIOS */}
      <div className="relative z-10 flex items-center justify-between mb-4 pb-2 border-b border-cyan-500/20">
        <div className="flex items-center gap-2.5">
          <div className="w-2.5 h-2.5 rounded-full bg-[var(--neon-cyan)] shadow-[0_0_12px_var(--neon-cyan)] animate-pulse" />
          <span className="font-[Orbitron] text-xs tracking-[0.3em] text-[var(--neon-cyan)] uppercase font-bold">
            Blade OS // BOOT LOADER
          </span>
        </div>
        <span className="text-[10px] text-gray-400 uppercase tracking-widest hidden sm:inline-block">
          [CLICK / SPACE TO SKIP]
        </span>
      </div>

      {/* TERMINAL SCROLL */}
      <div className="relative z-10 flex-1 overflow-hidden space-y-0.5">
        {visibleLines.map((l, i) => (
          <div key={i} className={`${TONE[l.tone]} leading-relaxed`}>
            {l.text || '\u00A0'}
          </div>
        ))}
        <span className="inline-block w-2 h-3.5 bg-[var(--neon-cyan)] animate-pulse ml-0.5 align-middle" />
      </div>

      {/* PROGRESS BAR */}
      <div className="relative z-10 mt-4 pt-3 border-t border-cyan-500/20">
        <div className="flex justify-between text-xs mb-1.5 font-semibold">
          <span className="text-[#64748b] font-mono tracking-wider">SYSTEM INITIALIZATION</span>
          <span className="text-[#00f0ff] font-mono">{Math.floor(progress).toString().padStart(3, '0')}%</span>
        </div>
        <div className="w-full h-1.5 bg-[#0b1329] rounded-full overflow-hidden border border-cyan-500/20">
          <div
            className="h-full bg-gradient-to-r from-[#00f0ff] via-[#a855f7] to-[#ff2a6d] shadow-[0_0_12px_rgba(0,240,255,0.8)]"
            style={{ width: `${progress}%`, transition: 'width 0.05s linear' }}
          />
        </div>
      </div>

      {/* FOOTER SPECS */}
      <div className="relative z-10 mt-3 flex flex-wrap justify-between text-[10px] font-mono text-[#64748b] tracking-wider">
        <span>BIOS: v4.9.0-QUANTUM</span>
        <span>CPU: QUANTUM-X9 128C</span>
        <span>RAM: 512GB Q-RAM</span>
        <span>NET: 100Gbps LINK OK</span>
      </div>
    </div>
  );
}